/**
 * The origin of a URL represents the base location from which a
 * resource is served and includes both the scheme and authority
 * components.
 *
 * Example
 * =======
 *
 * URL: https://www.example.com/page.html
 * In this case:
 * + Scheme: HTTPS
 * + Authority: www.example.com
 * Together, they form the origin URL or simply the origin.
 *
 * @param url
 * @returns string
 */
export const getUrlOrigin = (url: string): string => {
  // Remove the protocol (e.g., 'http://' or 'https://')
  let origin = url.replace(/(^\w+:|^)\/\//, '');

  // Remove any path after the domain
  origin = origin.split('/')[0];

  return origin;
};

export const isUrl = (str: string): boolean => {
  const hasScheme = str.startsWith('https://') || str.startsWith('http://');

  const urlPattern =
    /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d{1,5})?(\/\S*)?$/i;

  return hasScheme && urlPattern.test(str);
};

// The site name to show for a URL: its host without a leading "www.", or
// undefined when the string isn't a valid URL.
export const hostOf = (url: string): string | undefined => {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return undefined;
  }
};

// The site name for an address that looks finished: http or https, and a
// host ending in a dot plus at least two letters ("spotify.com" yes,
// "spotify" no). Lets a preview wait until the whole address is typed.
export const hostOfCompleteUrl = (url: string): string | undefined => {
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return undefined;
  }
  try {
    const hostname = new URL(trimmed).hostname.replace(/^www\./, '');
    return /\.[a-z]{2,}$/i.test(hostname) ? hostname : undefined;
  } catch {
    return undefined;
  }
};
